import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet, useNavigate, useParams } from 'react-router-dom';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { 
  Building2, 
  Users, 
  FileText,
  ChevronRight,
  ChevronDown,
  ShoppingCart,
  UserCog,
  Calculator,
  Briefcase,
  Package,
  GitBranch,
  Loader2,
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  BarChart3,
  MessageSquare,
  Layers,
  Clock,
  HardHat,
  Wrench,
  Percent
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import PanelHeader from '../../components/PanelHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BoardPanelContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { getBadgeCount, markSeen } = useBadges();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBranches, setExpandedBranches] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname, fetchUnreadCounts]);

  const departmentItems = [
    { key: 'satin-alma', icon: ShoppingCart, label: 'Satın Alma', menuKey: 'purchasing', badgeKey: null },
    { key: 'alinan-hizmetler', icon: Wrench, label: 'Alınan Hizmetler', menuKey: 'services', badgeKey: null },
    { key: 'insan-kaynaklari', icon: UserCog, label: 'İnsan Kaynakları', menuKey: 'hr', badgeKey: 'board_pending' },
    { key: 'muhasebe', icon: Calculator, label: 'Muhasebe', menuKey: 'accounting', badgeKey: 'board_muhasebe' },
    { key: 'idari-isler', icon: Briefcase, label: 'İdari İşler', menuKey: 'admin', badgeKey: null },
    { key: 'depo', icon: Package, label: 'Depo', menuKey: 'depot', badgeKey: null },
    { key: 'is-akisi', icon: GitBranch, label: 'İş Akışı', menuKey: 'workflow', badgeKey: null },
  ];

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/board/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        
        // Auto-expand if a branch is selected
        if (branchId) {
          setExpandedBranches(prev => ({ ...prev, [branchId]: true }));
        }
      } else {
        toast.error('Şubeler yüklenemedi');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const toggleBranch = (branchId) => {
    setExpandedBranches(prev => ({
      ...prev,
      [branchId]: !prev[branchId]
    }));
  };

  const isActiveDepartment = (bId, deptKey) => {
    return location.pathname.includes(`/board/branch/${bId}/${deptKey}`);
  };

  const isActiveBranch = (bId) => {
    return location.pathname.includes(`/board/branch/${bId}`);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="board-panel">
      {/* Header */}
      <PanelHeader
        title="Yönetim Kurulu Paneli"
        titleIcon={Building2}
        titleColor="text-[hsl(var(--brand-700))]"
      />

      <div className="pt-16">
        {/* Sidebar - Hover'da genişleyen */}
        <aside 
          className={`fixed top-16 left-0 h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white transition-all duration-300 ease-in-out z-40 ${
            sidebarExpanded ? 'w-64' : 'w-14'
          }`}
          onMouseEnter={() => setSidebarExpanded(true)}
          onMouseLeave={() => setSidebarExpanded(false)}
        >
          <div className={`p-2 border-b border-[hsl(var(--border))] ${sidebarExpanded ? 'px-4' : 'px-2'}`}>
            <Link to="/board">
              <Button 
                variant={location.pathname === '/board' || location.pathname === '/board/dashboard' ? 'default' : 'ghost'} 
                className={`w-full justify-start gap-2 ${sidebarExpanded ? '' : 'px-2'}`}
                data-testid="board-dashboard-link"
                title="Genel Bakış"
              >
                <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
                {sidebarExpanded && <span>Genel Bakış</span>}
              </Button>
            </Link>
          </div>
          
          <div className={`p-2 ${sidebarExpanded ? 'px-4' : 'px-2'}`}>
            {sidebarExpanded && (
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Şubeler
              </h3>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-500))]" />
              </div>
            ) : (
              <ScrollArea className="h-[calc(100vh-14rem)]">
                <nav className="space-y-1">
                  {branches.map((branch) => (
                    <Collapsible
                      key={branch.id}
                      open={sidebarExpanded && expandedBranches[branch.id]}
                      onOpenChange={() => sidebarExpanded && toggleBranch(branch.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-between px-2 py-2 rounded-md transition-colors ${
                            isActiveBranch(branch.id)
                              ? 'bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-800))]'
                              : 'hover:bg-[hsl(var(--neutral-100))] text-gray-700'
                          }`}
                          data-testid={`branch-${branch.id}`}
                          title={branch.name}
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 flex-shrink-0" />
                            {sidebarExpanded && (
                              <span className="font-medium text-sm truncate max-w-[120px]">{branch.name}</span>
                            )}
                          </div>
                          {sidebarExpanded && (
                            <div className="flex items-center gap-2">
                              {branch.pending_requests > 0 && (
                                <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                                  {branch.pending_requests}
                                </Badge>
                              )}
                              {expandedBranches[branch.id] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </div>
                          )}
                          {!sidebarExpanded && branch.pending_requests > 0 && (
                            <Badge variant="destructive" className="h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                              {branch.pending_requests}
                            </Badge>
                          )}
                        </button>
                      </CollapsibleTrigger>
                      
                      {sidebarExpanded && (
                        <CollapsibleContent>
                          <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-[hsl(var(--border))] pl-3">
                            <Link
                              to={`/board/branch/${branch.id}/depo`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                                location.pathname.includes(`/board/branch/${branch.id}/depo`)
                                  ? 'bg-[hsl(var(--brand-700))] text-white'
                                  : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                              }`}
                              data-testid={`depo-${branch.id}`}
                            >
                              <Package className="h-4 w-4" />
                              <span>Depo</span>
                            </Link>
                            <Link
                              to={`/board/branch/${branch.id}/sube-yonetim`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                                location.pathname.includes(`/board/branch/${branch.id}/sube-yonetim`)
                                  ? 'bg-[hsl(var(--brand-700))] text-white'
                                  : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                              }`}
                              data-testid={`sube-yonetim-${branch.id}`}
                            >
                              <Building2 className="h-4 w-4" />
                              <span>Şube Yönetim</span>
                            </Link>
                          </div>
                        </CollapsibleContent>
                      )}
                    </Collapsible>
                  ))}
                  
                  {/* Birimler Bölümü */}
                  <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                    {sidebarExpanded && (
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                        Birimler
                      </h3>
                    )}
                    <div className="space-y-0.5">
                      <Link
                        to="/board/birimler/insan-kaynaklari"
                        className={`flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors ${
                          location.pathname.includes('/board/birimler/insan-kaynaklari')
                            ? 'bg-[hsl(var(--brand-700))] text-white'
                            : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                        }`}
                        data-testid="birim-insan-kaynaklari"
                        title="İnsan Kaynakları"
                      >
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4 flex-shrink-0" />
                          {sidebarExpanded && <span>İnsan Kaynakları</span>}
                        </div>
                      </Link>
                      <Link
                        to="/board/birimler/muhasebe"
                        className={`flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors ${
                          location.pathname.includes('/board/birimler/muhasebe')
                            ? 'bg-[hsl(var(--brand-700))] text-white'
                            : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                        }`}
                        data-testid="birim-muhasebe"
                        title="Muhasebe"
                      >
                        <div className="flex items-center gap-2">
                          <Calculator className="h-4 w-4 flex-shrink-0" />
                          {sidebarExpanded && <span>Muhasebe</span>}
                        </div>
                      </Link>
                      <Link
                        to="/board/birimler/iletisim"
                        className={`flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors ${
                          location.pathname.includes('/board/birimler/iletisim')
                            ? 'bg-[hsl(var(--brand-700))] text-white'
                            : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                        }`}
                        data-testid="birim-iletisim"
                        title="İletişim"
                      >
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 flex-shrink-0" />
                          {sidebarExpanded && <span>İletişim</span>}
                        </div>
                      </Link>
                      <Link
                        to="/board/birimler/satin-alma"
                        className={`flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors ${
                          location.pathname.includes('/board/birimler/satin-alma')
                            ? 'bg-[hsl(var(--brand-700))] text-white'
                            : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                        }`}
                        data-testid="birim-satin-alma"
                        title="Satın Alma"
                      >
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4 flex-shrink-0" />
                          {sidebarExpanded && <span>Satın Alma</span>}
                        </div>
                      </Link>
                      <Link
                        to="/board/birimler/faiz-hesaplama"
                        className={`flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors ${
                          location.pathname.includes('/board/birimler/faiz-hesaplama')
                            ? 'bg-[hsl(var(--brand-700))] text-white'
                            : 'hover:bg-[hsl(var(--neutral-100))] text-gray-600'
                        }`}
                        data-testid="birim-faiz-hesaplama"
                        title="Faiz Hesaplama"
                      >
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4 flex-shrink-0" />
                          {sidebarExpanded && <span>Faiz Hesaplama</span>}
                        </div>
                      </Link>
                    </div>
                  </div>
                </nav>
              </ScrollArea>
            )}
          </div>
        </aside>

        {/* Main Content - Sidebar genişliğine göre margin */}
        <main className={`flex-1 p-6 pt-2 transition-all duration-300 ${sidebarExpanded ? 'ml-64' : 'ml-14'}`}>
          <Outlet context={{ branches }} />
        </main>
      </div>
    </div>
  );
};

const BoardPanel = () => {
  return (
    <BadgeProvider panel="board">
      <BoardPanelContent />
    </BadgeProvider>
  );
};

export default BoardPanel;
